import React, { useState, useEffect } from 'react';

const LoginPage = () => {
    const navigate = (path) => {
        window.location.href = path;
    };
    
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [crowAnswer, setCrowAnswer] = useState('');
    const [error, setError] = useState('');
    const [showWelcome, setShowWelcome] = useState(false);
    const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
    const [showCrowQuestion, setShowCrowQuestion] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setShowWelcome(true);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            
            const requestBody = { username, password };
            
            // Include totpCode if we're in the 2FA step or beyond
            if ((showTwoFactorModal || showCrowQuestion) && totpCode) {
                requestBody.totpCode = totpCode;
            }

            // Include crowAnswer if we're in the crow question step
            if (showCrowQuestion && crowAnswer) {
                requestBody.crowAnswer = crowAnswer;
            }

            const response = await fetch(`${window.backendUrl}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();

            if (response.status === 200) {
                if (data.requiresTwoFactor) {
                    setShowTwoFactorModal(true);
                } else if (data.requiresCrowQuestion) {
                    setShowCrowQuestion(true);
                } else {
                    // Set company from backend response
                    if (data.company) {
                        localStorage.setItem('company', data.company);
                    }
                    navigate('/manage-orders');
                }
            } else {
                setError(data.error || 'Login failed');
                
                // Clear appropriate fields based on which step failed
                if (showCrowQuestion && response.status === 401) {
                    setCrowAnswer(''); // Clear crow answer for retry
                } else if (showTwoFactorModal && response.status === 401) {
                    setTotpCode(''); // Clear TOTP code for retry
                }
            }
        } catch (err) {
            console.error('Network error:', err);
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTwoFactorSubmit = (e) => {
        e.preventDefault();
        console.log('2FA form submitted with code:', totpCode);
        if (totpCode.length === 6) {
            handleSubmit(e);
        } else {
            setError('Please enter a valid 6-digit code');
        }
    };

    const handleCrowQuestionSubmit = (e) => {
        e.preventDefault();
        console.log('Crow question form submitted with answer:', crowAnswer);
        if (crowAnswer.trim()) {
            handleSubmit(e);
        } else {
            setError('Please enter an answer');
        }
    };

    const handleModalClose = () => {
        setShowTwoFactorModal(false);
        setShowCrowQuestion(false);
        setTotpCode('');
        setCrowAnswer('');
        setError('');
    };

    const handleTotpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setTotpCode(value);
        if (error) setError(''); // Clear error when user types
    };

    const handleCrowAnswerChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        setCrowAnswer(value);
        if (error) setError(''); // Clear error when user types
    };

    return (
        <>
            {/* Bootstrap CSS */}
            <link 
                href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css" 
                rel="stylesheet" 
            />
            
            <div className="container d-flex align-items-center justify-content-center min-vh-100">
                <div className="row w-100">
                    <div className="col-md-6 col-lg-4 mx-auto">
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <svg
                                viewBox="0 0 300 100"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ width: '100%', height: '150px' }}
                            >
                                <path
                                    d="M 10 50 Q 80 10, 150 50 Q 220 90, 290 50"
                                    style={{
                                        stroke: '#007bff',
                                        strokeWidth: '2',
                                        fill: 'none',
                                        strokeDasharray: '100',
                                        strokeDashoffset: showWelcome ? '0' : '1000',
                                        transition: 'stroke-dashoffset 2s ease-in-out',
                                    }}
                                />
                                <text
                                    x="50%"
                                    y="50%"
                                    style={{
                                        fill: showWelcome ? '#007bff' : 'transparent',
                                        stroke: '#007bff',
                                        strokeWidth: '1',
                                        fontSize: '40px',
                                        fontWeight: 'bold',
                                        textAnchor: 'middle',
                                        dominantBaseline: 'middle',
                                        opacity: showWelcome ? 1 : 0,
                                        transition: 'opacity 2s ease-in-out, fill 2s ease-in-out',
                                        transitionDelay: '1s',
                                    }}
                                >
                                    FlowTech
                                </text>
                            </svg>
                        </div>
                        
                        {error && <div className="alert alert-danger">{error}</div>}
                        
                        <form onSubmit={handleSubmit} autoComplete="off">
                            <div className="form-group mb-3">
                                <label className="form-label">Username</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter your username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    name="userIdentifier"
                                    autoComplete="off"
                                    disabled={showTwoFactorModal || showCrowQuestion}
                                    required
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label">Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    name="userAccessCode"
                                    autoComplete="new-password"
                                    disabled={showTwoFactorModal || showCrowQuestion}
                                    required
                                />
                            </div>
                            <button 
                                type="submit" 
                                className="btn btn-primary w-100 mt-4"
                                disabled={isLoading || showTwoFactorModal || showCrowQuestion}
                            >
                                {isLoading && !showTwoFactorModal && !showCrowQuestion ? 'Signing in...' : 'Login'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Two-Factor Authentication Modal */}
            {showTwoFactorModal && !showCrowQuestion && (
                <div 
                    className="modal fade show" 
                    style={{ 
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999
                    }}
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Two-Factor Authentication</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={handleModalClose}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-4">
                                    Please enter the 6-digit code
                                </p>
                                
                                <form onSubmit={handleTwoFactorSubmit}>
                                    <div className="mb-5">
                                        <label className="form-label">Authentication Code: </label>
                                        <input
                                            type="text"
                                            className="form-control text-center"
                                            placeholder="000000"
                                            value={totpCode}
                                            onChange={handleTotpChange}
                                            maxLength="6"
                                            style={{ 
                                                fontSize: '1.5rem', 
                                                letterSpacing: '0.3rem',
                                                fontFamily: 'monospace'
                                            }}
                                            autoComplete="off"
                                            autoFocus
                                            required
                                        />
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary flex-fill"
                                            onClick={handleModalClose}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary flex-fill"
                                            disabled={isLoading || totpCode.length !== 6}
                                        >
                                            {isLoading ? 'Verifying...' : 'Verify'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Crow Question Modal */}
            {showCrowQuestion && (
                <div 
                    className="modal fade show" 
                    style={{ 
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999
                    }}
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Final Verification</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={handleModalClose}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-4 text-center" style={{ fontSize: '1.1rem' }}>
                                    <strong>How many crows will fly from Rajlokkhi to Ajompur at 11:37 pm today?</strong>
                                </p>
                                
                                <form onSubmit={handleCrowQuestionSubmit}>
                                    <div className="mb-4">
                                        <label className="form-label">Your Answer:</label>
                                        <input
                                            type="text"
                                            className="form-control text-center"
                                            placeholder="Enter number"
                                            value={crowAnswer}
                                            onChange={handleCrowAnswerChange}
                                            style={{ 
                                                fontSize: '1.3rem', 
                                                fontFamily: 'monospace'
                                            }}
                                            autoComplete="off"
                                            autoFocus
                                            required
                                        />
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary flex-fill"
                                            onClick={handleModalClose}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary flex-fill"
                                            disabled={isLoading || !crowAnswer}
                                        >
                                            {isLoading ? 'Verifying...' : 'Submit'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LoginPage;